import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ProductImageGallery = ({ images = [], videos = [], productName = '' }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  const allMedia = [...images, ...videos];
  const currentMedia = allMedia[currentIndex] || {};
  const isVideo = videos.some(video => video.id === currentMedia.id);

  const minSwipeDistance = 50;

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') handlePrevious();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'Escape') setIsZoomed(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex]);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % allMedia.length);
    setIsZoomed(false);
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + allMedia.length) % allMedia.length);
    setIsZoomed(false);
  };

  const handleThumbnailClick = (index) => {
    setCurrentIndex(index);
    setIsZoomed(false);
  };

  const handleMouseMove = (e) => {
    if (!isZoomed || !imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setZoomPosition({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
  };

  const handleImageClick = () => {
    if (!isVideo) {
      setIsZoomed(!isZoomed);
    }
  };

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) handleNext();
    if (isRightSwipe) handlePrevious();
  };

  if (allMedia.length === 0) {
    return (
      <div className="w-full h-96 bg-muted rounded-lg flex items-center justify-center">
        <div className="text-center">
          <Icon name="Image" size={48} className="mx-auto mb-2 text-text-secondary opacity-50" />
          <p className="text-text-secondary">No images available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Main Image/Video Display */}
      <div 
        ref={containerRef}
        //className="relative bg-surface border border-border rounded-lg overflow-hidden group"
      >
        <div 
          className="relative w-full h-96 md:h-[500px] lg:h-[600px] cursor-pointer"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {isVideo ? (
            <video
              src={currentMedia.url}
              poster={currentMedia.thumbnail}
              controls
              className="w-full h-full object-cover"
              aria-label={`Product video for ${productName}`}
            >
              Your browser does not support the video tag.
            </video>
          ) : (
            <div
              ref={imageRef}
              className={`relative w-full h-full overflow-hidden ${isZoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'}`}
              onClick={handleImageClick}
              onMouseMove={handleMouseMove}
              onMouseLeave={() => setIsZoomed(false)}
            >
              <Image
                src={currentMedia?.file_path}
                className={`w-full h-[300px] sm:h-[400px] lg:w-[560px] lg:h-[476px] object-cover rounded-[12px] ${
                  isZoomed 
                    ? 'scale-200' :'hover:scale-105'
                }`}
                alt={currentMedia.alt || `${productName} - Image ${currentIndex + 1}`}
            
                style={isZoomed ? {
                  transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`
                } : {}}
              />
              
              {/* Zoom Indicator */}
              {!isZoomed && (
                <div className="absolute top-4 right-4 bg-black/50 text-white px-2 py-1 rounded-md text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                  <Icon name="ZoomIn" size={14} className="inline mr-1" />
                  Click to zoom
                </div>
              )}
            </div>
          )}

          {/* Navigation Arrows */}
          {allMedia.length > 1 && (
            <>
              <button
                onClick={handlePrevious}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-white"
                aria-label="Previous image"
              >
                <Icon name="ChevronLeft" size={20} />
              </button>
              
              <button
                onClick={handleNext}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-white"
                aria-label="Next image"
              >
                <Icon name="ChevronRight" size={20} />
              </button>
            </>
          )}

          {/* Media Counter */}
          <div className="absolute bottom-4 left-4 bg-black/50 text-white px-3 py-1 rounded-md text-sm">
            {currentIndex + 1} / {allMedia.length}
          </div>

          {/* Video Indicator */}
          {isVideo && (
            <div className="absolute top-4 left-4 bg-black/50 text-white px-2 py-1 rounded-md text-xs flex items-center space-x-1">
              <Icon name="Play" size={12} />
              <span>Video</span>
            </div>
          )}
        </div>
      </div>

      {/* Thumbnail Navigation */}
      {allMedia.length > 1 && (
        <div className="flex space-x-2 overflow-x-auto pb-2">
          {allMedia.map((media, index) => (
            <button
              key={media.id}
              onClick={() => handleThumbnailClick(index)}
              className={`flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden border-2 transition-all ${
                index === currentIndex
                  ? 'border-primary shadow-md'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              <div className="relative w-full h-full">
                <Image
                  src={media?.thumbnail || media?.file_path}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover rounded-[12px]"
                />
                {videos.some(video => video.id === media.id) && (
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                    <Icon name="Play" size={16} className="text-white" />
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Mobile Swipe Indicator */}
      <div className="md:hidden text-center">
        <p className="text-xs text-text-secondary flex items-center justify-center space-x-1">
          <Icon name="Hand" size={12} />
          <span>Swipe to navigate</span>
        </p>
      </div>
    </div>
  );
};

export default ProductImageGallery;